import { differenceInYears } from "date-fns";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { SourceTransitionalState } from "../components/fields/PPRadioGroup";
import {
  AppContextType,
  ListViewContextType,
  ListViewsContextType,
} from "../context/ApplicationContext";
import { loadFormConfiguration } from "../store/load-form-slice";
import store from "../store/store";
import { evaluateExpression } from "./parser";
import { fabClasses } from "@mui/material";

let map = new Map<string, (...args: any[]) => void>();

export const funcExecutor = (
  funcName: string,
  args: any[],
  getValues: UseFormGetValues<FieldValues>,
  setValue: UseFormSetValue<FieldValues>,
  appContext: AppContextType,
  sourceTranistionalState: SourceTransitionalState | undefined = {
    previousValue: undefined,
    currentValue: undefined,
  },
  lstContext: ListViewsContextType | undefined = undefined,
  setLstContext:
    | React.Dispatch<React.SetStateAction<ListViewsContextType>>
    | undefined = undefined,
  queryParams: URLSearchParams | undefined = undefined
) => {
  const values: FieldValues = getValues();

  const setValueWhen = (targetField: string, value: string, when: string) => {
    const argArray: string[] = when.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (result) {
      setRadioValueAndDispatchEvent(targetField, value);
    }
  };

  const setRadioValueAndDispatchEvent = (
    targetFieldId: string,
    value: string | number
  ): void => {
    //console.log(targetFieldId)
    let input = (Array.from(document.getElementsByName(targetFieldId)).filter(
      (v) => {
       // console.log(v);

       // console.log((v as HTMLInputElement).value +':'+value)
        return (v as HTMLInputElement).value === value;
      }
    )[0] as HTMLInputElement);

    input.click();
    appContext.fieldState[targetFieldId] = true;
  };

  const setValueAndDispatchEvent = (
    targetFieldId: string,
    value: string | number
  ): void => {
    let input = document.getElementById(targetFieldId);
    let nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    )?.set;
    nativeInputValueSetter?.call(input, value);
    let ev2 = new Event("input", { bubbles: true });
    input?.dispatchEvent(ev2);
  };

  const calculateAge = (
    targetField: string,
    sourceField: string,
    values: FieldValues,
    setValue: UseFormSetValue<FieldValues>
  ): void => {
    const dob: Date = new Date(values[sourceField]);
    setValue(targetField, differenceInYears(Date.now(), dob));
    setValueAndDispatchEvent(targetField, differenceInYears(Date.now(), dob));
  };

  const resetField = (targetField: string) => setValue(targetField, undefined);

  const resetFields = (
    targetFields: string[],
    resetWhen: string | undefined = undefined
  ): void => {
    let doReset: boolean = false;
    if (resetWhen == undefined) doReset = true;
    else if (evaluateExpression(resetWhen.split(" "), values)) doReset = true;
    if (doReset)
      targetFields.forEach((targetField: string) => resetField(targetField));
  };

  const toggleCheckbox = (
    targetField: string,
    toggleWhen: string | undefined = undefined
  ) => {
    let doToggle: boolean =
      !toggleWhen || evaluateExpression(toggleWhen.split(" "), values);

    if (doToggle) {
      appContext.checkboxChecked.checked[targetField] =
        !appContext.checkboxChecked.checked[targetField];
    }
  };

  const resetErrorState = (targetFields: string[]) => {
    targetFields.forEach((field: string) => {
      appContext.fieldState[field] = true;
    });
  };

  const hideFieldsWhen = (
    targetFields: string[],
    hidewhen: string,
    sourceField: string
  ): void => {
    const argArray: string[] = hidewhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    targetFields.forEach(
      (targetField: string) => (appContext.fieldHide[targetField] = result)
    );
  };

  const copyFieldsWhen = (
    targetFields: string[],
    sourceFields: string[],
    copyWhen: string
  ): void => {
    const argArray: string[] = copyWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (sourceFields.length == targetFields.length) {
      sourceFields.forEach((sourceField: string, index: number) => {
        setValue(targetFields[index], values[sourceField]);
      });
    }
  };

  const disableEnableFieldsWhen = (
    targetFields: string[],
    disableWhen: string,
    enable: string
  ): void => {
    const argArray: string[] = disableWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    targetFields.forEach((targetField: string) => {
      appContext.fieldDisable[targetField] = /^true$/i.test(enable)
        ? !result
        : result;
    });
  };

  const setTextFieldValueWhen = (
    sourceField: string,
    targetField: string,
    linkWhen: string
  ): void => {
    const argArray: string[] = linkWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (result) setValue(targetField, values[sourceField]);
  };

  const disableEnableStepWhen = (sectionId: string, disableWhen: string) => {
    //console.log(disableWhen);
    const argArray: string[] = disableWhen.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    appContext.sectionState.hideState[sectionId] = result;
  };

  const setMinimumNumberOfRows = (...args: any[]): void => {
    if (args.length > 0 && args.length % 2 == 2) return;

    if (args.length > 0 && lstContext) {
      const sectionId: string = args.shift();
      while (args.length > 0) {
        const when: string[] = args.shift().split(" ");
        const value: string = args.shift();
        const result: boolean = evaluateExpression(when.slice(), values);
        if (result) {
          lstContext[sectionId].minimumNumberOfRows = +value;
          return;
        }
      }
    }
  };

  // loadForm needs refactoring to accomodate ListView loads
  // loadForm needs refactoring to set field values other than strings

  const loadForm = (fieldId: string): void => {
    if (fieldId) {
      store.dispatch(
        loadFormConfiguration(
          queryParams ?? new URLSearchParams(),
          getValues(fieldId),
          setValue
        )
      );
    }
  };

  const clearListView = (sectionId: string, when: string): void => {
   // console.log(sectionId);
    const argArray: string[] = when.split(" ");
    const result: boolean = evaluateExpression(argArray, values);
    if (result) {
      let newListsContext = { ...lstContext };
      let tmpListCtx: ListViewContextType | undefined =
        newListsContext[sectionId];
      if (tmpListCtx) {
        tmpListCtx.errorState.message = undefined;
        tmpListCtx.errorState.state = false;
        tmpListCtx.dataModel = { uiModel: [], submitRecordModel: [] };
      }
      if (setLstContext) setLstContext({ ...newListsContext });
    }
  };

  const setCompleteStepState = (
    sectionId: string,
    state: string,
    when: string
  ): void => {
    const whenArray: string[] = when.split(" ");
    const result: boolean = evaluateExpression(whenArray, values);
    if (result) {
      const stateArray: string[] = state.split(" ");
      const isComplete: boolean = evaluateExpression(stateArray, values);
      appContext.sectionState.completeState[sectionId] = isComplete;
    }
  };

  map.set("calculateAge", calculateAge);
  map.set("hideFieldsWhen", hideFieldsWhen);
  map.set("disableEnableFieldsWhen", disableEnableFieldsWhen);
  map.set("setTextFieldValueWhen", setTextFieldValueWhen);
  map.set("toggleCheckbox", toggleCheckbox);
  map.set("resetFields", resetFields);
  map.set("resetErrorState", resetErrorState);
  map.set("copyFieldsWhen", copyFieldsWhen);
  map.set("disableEnableStepWhen", disableEnableStepWhen);
  map.set("setMinimumNumberOfRows", setMinimumNumberOfRows);
  map.set("loadForm", loadForm);
  map.set("clearListView", clearListView);
  map.set("setCompleteStepState", setCompleteStepState);
  map.set("setValueWhen", setValueWhen);

  const func: (...args: any[]) => void = map.get(funcName) ?? (() => {});
  func(...args, values, setValue);
};
